<div class="p-4 space-y-4 h-full overflow-y-auto">

  <div class="flex items-center gap-3">
    <h1 class="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
      <i class="fa-solid fa-thumbtack text-app-teal-100"></i>Pinned scanners
    </h1>
    <span class="text-xs text-gray-500 dark:text-gray-400 ml-2">
      Personal shortcuts — each pin becomes a sidebar entry that opens the URL as an iframe.
    </span>
    <span *ngIf="dirty()" class="text-[11px] text-amber-600 dark:text-amber-300 ml-auto">
      • unsaved changes
    </span>
  </div>

  <!-- Suggested pins (visible while there are suggestions the operator hasn't already added). -->
  <div *ngIf="!isSuggestedAlreadyPinned(SUGGESTED[0])"
    class="border border-gray-200 dark:border-[#434863] rounded p-3 bg-gray-50 dark:bg-app-black-100">
    <div class="text-xs text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-2">
      <i class="fa-solid fa-lightbulb text-amber-400"></i>
      Suggested pins — click to add:
    </div>
    <div class="flex flex-wrap gap-2">
      <button *ngFor="let sug of SUGGESTED"
        class="dark-btn dark-btn-sm"
        [disabled]="isSuggestedAlreadyPinned(sug)"
        (click)="addSuggested(sug)">
        <i class="fa-solid fa-plus mr-1"></i>{{ sug.label }}
        <span class="text-[10px] text-gray-500 dark:text-gray-400 ml-1 font-mono">({{ sug.url }})</span>
      </button>
    </div>
  </div>

  <!-- Existing pins list -->
  <div class="border border-gray-200 dark:border-[#434863] rounded overflow-hidden">
    <div class="px-3 py-2 border-b border-gray-200 dark:border-[#434863] bg-gray-50 dark:bg-app-black-100 flex items-center gap-2">
      <span class="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Your pins</span>
      <span class="text-xs text-gray-500 dark:text-gray-400 ml-2">{{ draftPins().length }} entries</span>
    </div>

    <div *ngIf="draftPins().length === 0" class="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
      No pins yet. Add one below, or click a Suggested pin above.
    </div>

    <div *ngIf="draftPins().length > 0" class="divide-y divide-gray-200 dark:divide-[#434863]">
      <div *ngFor="let pin of draftPins(); let i = index; trackBy: trackByIdx"
        class="p-3 flex items-center gap-2">

        <div class="flex flex-col gap-1 flex-none">
          <button class="dark-btn dark-btn-sm" [disabled]="i === 0" (click)="moveUp(i)" title="Move up">
            <i class="fa-solid fa-arrow-up"></i>
          </button>
          <button class="dark-btn dark-btn-sm" [disabled]="i >= draftPins().length - 1" (click)="moveDown(i)" title="Move down">
            <i class="fa-solid fa-arrow-down"></i>
          </button>
        </div>

        <div class="flex-1 grid grid-cols-1 md:grid-cols-[180px_180px_1fr] gap-2">
          <label class="block">
            <span class="text-[10px] uppercase text-gray-500 dark:text-gray-400">Label</span>
            <input type="text"
              class="mt-0.5 block w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 text-sm"
              [ngModel]="pin.label"
              (ngModelChange)="onPinLabelEdit(i, $event)" />
          </label>
          <label class="block">
            <span class="text-[10px] uppercase text-gray-500 dark:text-gray-400">Key (URL slug)</span>
            <input type="text"
              class="mt-0.5 block w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 text-sm font-mono"
              [ngModel]="pin.key"
              (ngModelChange)="onPinKeyEdit(i, $event)" />
          </label>
          <label class="block">
            <span class="text-[10px] uppercase text-gray-500 dark:text-gray-400">URL</span>
            <input type="text"
              class="mt-0.5 block w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 text-sm font-mono"
              [ngModel]="pin.url"
              (ngModelChange)="onPinUrlEdit(i, $event)" />
          </label>
        </div>

        <button class="dark-btn dark-btn-danger dark-btn-sm flex-none" (click)="removeAt(i)" title="Remove pin">
          <i class="fa-solid fa-trash"></i>
        </button>
      </div>
    </div>
  </div>

  <!-- Add-new form -->
  <div class="border border-gray-200 dark:border-[#434863] rounded p-3 bg-white dark:bg-app-black-100">
    <div class="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
      <i class="fa-solid fa-plus mr-1"></i>Add a pin
    </div>
    <div class="grid grid-cols-1 md:grid-cols-[1fr_1fr_2fr_auto] gap-2 items-end">
      <label class="block">
        <span class="text-[10px] uppercase text-gray-500 dark:text-gray-400">Label</span>
        <input type="text"
          class="mt-0.5 block w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 text-sm"
          [(ngModel)]="newLabel"
          placeholder="Internal Tracker" />
      </label>
      <label class="block">
        <span class="text-[10px] uppercase text-gray-500 dark:text-gray-400">Key (optional — derived from label)</span>
        <input type="text"
          class="mt-0.5 block w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 text-sm font-mono"
          [(ngModel)]="newKey"
          placeholder="internal-tracker" />
      </label>
      <label class="block">
        <span class="text-[10px] uppercase text-gray-500 dark:text-gray-400">URL</span>
        <input type="text"
          class="mt-0.5 block w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 text-sm font-mono"
          [(ngModel)]="newUrl"
          placeholder="https://f2-pmo-srvr.f2-tech.ai/f2-tracker/" />
      </label>
      <button class="dark-btn dark-btn-primary dark-btn-sm" (click)="addNew()">
        <i class="fa-solid fa-plus mr-1"></i>Add
      </button>
    </div>
    <p class="text-[10px] text-gray-500 dark:text-gray-400 mt-2">
      The pin appears in the draft list above. Nothing is saved until you click Save.
    </p>
  </div>

  <!-- Save/Revert/Cancel -->
  <div class="flex items-center gap-2 sticky bottom-0 py-3 bg-white dark:bg-app-black-200 border-t border-gray-200 dark:border-[#434863]">
    <button class="dark-btn dark-btn-primary" (click)="save()" [disabled]="saving() || !dirty()">
      <i class="fa-solid fa-floppy-disk mr-1"></i>
      <span *ngIf="!saving()">Save pinned scanners</span>
      <span *ngIf="saving()">Saving…</span>
    </button>
    <button class="dark-btn" (click)="revert()" [disabled]="saving() || !dirty()">
      <i class="fa-solid fa-rotate-left mr-1"></i>Revert
    </button>
    <a class="dark-btn ml-auto" routerLink="/admin/users">
      Back to admin
    </a>
  </div>
</div>
